import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { MemberUnavailability } from '../types/database';

export function useBandUnavailability(bandId: string | undefined) {
  const [unavailability, setUnavailability] = useState<MemberUnavailability[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUnavailability = useCallback(async () => {
    if (!bandId) return;

    const { data } = await supabase
      .from('member_unavailability')
      .select('*, profile:profiles(id, email, display_name)')
      .eq('band_id', bandId)
      .order('start_date', { ascending: true });

    setUnavailability((data as unknown as MemberUnavailability[]) ?? []);
    setLoading(false);
  }, [bandId]);

  useEffect(() => {
    fetchUnavailability();
  }, [fetchUnavailability]);

  return { unavailability, loading, refetch: fetchUnavailability };
}

export async function createUnavailability(
  input: {
    band_id: string;
    user_id: string;
    start_date: string;
    end_date: string;
    reason?: string;
  } | {
    band_id: string;
    user_id: string;
    start_date: string;
    end_date: string;
    reason?: string;
  }[]
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('member_unavailability')
    .insert(input);

  return { error: error?.message ?? null };
}

export async function deleteUnavailability(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('member_unavailability')
    .delete()
    .eq('id', id);

  return { error: error?.message ?? null };
}

/** Check if any band members are unavailable on a given date */
export function getUnavailableMembers(
  unavailability: MemberUnavailability[],
  date: string
): MemberUnavailability[] {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  return unavailability.filter((u) => {
    const start = new Date(u.start_date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(u.end_date);
    end.setHours(23, 59, 59, 999);
    return checkDate >= start && checkDate <= end;
  });
}
